import * as React from 'react'
import { Loader2 } from 'lucide-react'
import type { BillingPurchaseDto } from '@lib/dto/billing.dto'
import type { Uuid } from '@lib/validators/params'
import { formatCents } from '@lib/helpers/money'
import { toast } from '@/components/ui/toast'
import { useBillingActions, useBillingPurchase } from '@/hooks/useBilling'

/** How long to wait for Stripe to settle a payment before saying it can take a minute. */
const GIVE_UP_MS = 30_000

/**
 * Says how a purchase back from Checkout ended, and returns true once it has.
 * Returns false while it's still worth waiting.
 */
function announce(purchase: BillingPurchaseDto | undefined, unreadable: boolean, timedOut: boolean): boolean {
  if (purchase?.status === 'pending' && purchase.failureMessage) {
    toast(purchase.failureMessage)
    return true
  }
  switch (purchase?.status) {
    case 'succeeded':
      toast.success(`Added ${formatCents(purchase.creditCents)} of credit`)
      return true
    case 'cancelled':
      toast('Checkout cancelled. You weren’t charged.')
      return true
    case 'expired':
    case 'failed':
      toast('The payment didn’t go through.')
      return true
    default:
      if (!unreadable && !timedOut) return false
      toast('Payment received? It can take a minute to show here.')
      return true
  }
}

interface CheckoutReturnProps {
  purchaseId: Uuid
  /** Stripe's back link: the Owner left Checkout without paying. */
  cancelled: boolean
  /** Clears the purchase from the URL once its outcome is shown. */
  onDone: () => void
}

/**
 * Back from Stripe Checkout (`?purchase=<id>`): waits for the payment to
 * settle, polling because Stripe's webhook may land a moment later, then says
 * how it went. From the back link (`&cancelled=1`) it closes the Checkout page
 * first; a purchase paid in the meantime is credited instead.
 */
export function CheckoutReturn({ purchaseId, cancelled, onDone }: CheckoutReturnProps) {
  const { cancelPurchase, refresh } = useBillingActions()
  const polled = useBillingPurchase(cancelled ? null : purchaseId)
  const { mutate: cancel } = cancelPurchase
  const [timedOut, setTimedOut] = React.useState(false)
  const cancelSentRef = React.useRef(false)
  const doneRef = React.useRef(false)
  const purchase = cancelled ? cancelPurchase.data : polled.data
  const unreadable = cancelled ? cancelPurchase.isError : polled.isError

  React.useEffect(() => {
    if (!cancelled || cancelSentRef.current) return
    cancelSentRef.current = true
    cancel(purchaseId)
  }, [cancelled, purchaseId, cancel])

  React.useEffect(() => {
    const timer = window.setTimeout(() => setTimedOut(true), GIVE_UP_MS)
    return () => window.clearTimeout(timer)
  }, [])

  React.useEffect(() => {
    if (doneRef.current || !announce(purchase, unreadable, timedOut)) return
    doneRef.current = true
    if (purchase?.status === 'succeeded') void refresh()
    onDone()
  }, [purchase, unreadable, timedOut, refresh, onDone])

  return (
    <p role="status" className="flex items-center gap-2 text-body text-muted-foreground">
      <Loader2 className="size-4 animate-spin" />
      {cancelled ? 'Closing the checkout…' : 'Confirming your payment…'}
    </p>
  )
}
